import React, { useState } from "react";
import CustomButton from "components/common/Button/CustomButton";
import { USER_ROLE } from "Constants/constant";
import { roleAccess } from "helpers/RolesPermission";
import { BiPencil } from "react-icons/bi";
import { FaClone } from "react-icons/fa";
import { MdPublish } from "react-icons/md";
import { connect } from "react-redux";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import api from "store/services/superAdmin.services";
import { updateAllFormTemplatesLocally } from "store/actions/sp.actions";
import SweetAlertConfirmationModal from "components/common/SweetAlertConfirmation/SweetAlertConfirmation";
import { HiEye, HiEyeOff, HiTrash } from "react-icons/hi";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import { AiOutlineEyeInvisible } from "react-icons/ai";
import { apiDeleteRequest, apiPatchRequest } from "helpers/Requests";
import { navigateToClonedFormDraft } from "components/DynamicForms/shared/formTemplateClone";
import FormTemplateCloneModal from "components/DynamicForms/shared/FormTemplateCloneModal";
import { getUrlRole } from "Utils/utils";
import useCareLabels from "hooks/useCareLabels";
import { resolveFormTemplateRowStatus } from "components/DynamicForms/shared/formCategoryUtils";

const buildFormTemplateActionUrl = (action, templateUuid, routeRole) => {
  if (roleAccess([USER_ROLE.Super_Admin])) {
    if (action === "destroy") {
      return routeRole === "hrm"
        ? `/super-admin/hrm/form-template/${templateUuid}`
        : `/super-admin/form-template/${templateUuid}`;
    }

    return routeRole === "hrm"
      ? `/super-admin/hrm/form-templates/${action}/${templateUuid}`
      : `/super-admin/form-templates/${action}/${templateUuid}`;
  }

  const rolePrefix = getUrlRole();
  if (action === "destroy") {
    return routeRole === "hrm"
      ? `/${rolePrefix}/${routeRole}/form-templates/destroy/${templateUuid}`
      : `/${rolePrefix}/form-templates/destroy/${templateUuid}`;
  }

  return routeRole === "hrm"
    ? `/${rolePrefix}/${routeRole}/form-templates/${action}/${templateUuid}`
    : `/${rolePrefix}/form-templates/${action}/${templateUuid}`;
};

const FormTemplateListActionButtons = (props) => {
  const {
    formTemplateStatus,
    template,
    editFormTemplateHandler,
    updateFormTemplateLocally,
    routeRole,
    isUserGracePeriodOver,
    handleFetch,
    selectedFormCategories,
  } = props;
  const { participantLabel } = useCareLabels();
  const navigate = useNavigate();

  const [selectedPublishTemp, setSelectedPublishTemp] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showConfirmationModalPublish, setShowConfirmationModalPublish] =
    useState(false);
  const [showConfirmationModalDelete, setShowConfirmationModalDelete] =
    useState(false);

  const templateStatus = resolveFormTemplateRowStatus(
    template,
    formTemplateStatus?.value
  );

  const refreshList = () => {
    const payload = {
      status: formTemplateStatus?.value ?? "all",
      page: 1,
    };

    if (selectedFormCategories?.length > 0) {
      payload.form_category = selectedFormCategories;
    }

    handleFetch(payload, null);
  };

  const [showCloneModal, setShowCloneModal] = useState(false);

  const handleCloneCompleted = ({ clonedForm, sourceTitle }) => {
    navigateToClonedFormDraft(navigate, {
      role: routeRole,
      routeRole,
      clonedForm,
      sourceTitle,
    });
    refreshList();
  };

  const viewFormTemplateHandler = () => {
    if (roleAccess([USER_ROLE.Super_Admin])) {
      navigate(
        `/${routeRole}/view-template/${template.uuid}/${templateStatus}`
      );
    } else if (roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])) {
      navigate(
        `/view-template/${routeRole}/${template.slug}/${templateStatus}/read-only`
      );
    }
  };

  const handleHideTemplate = async () => {
    try {
      const url = buildFormTemplateActionUrl("hide", template.uuid, routeRole);

      await apiPatchRequest(url, { is_hidden: true });

      toast.success("Form has been hidden successfully!");

      updateFormTemplateLocally({ id: template.uuid, type: "hide" });

      refreshList();
    } catch (err) {
      toast.error(err?.message || "Something went wrong while hiding form!");
      console.log("err", err);
    }
  };

  const handleUnhideTemplate = async () => {
    try {
      const url = buildFormTemplateActionUrl("unhide", template.uuid, routeRole);
      await apiPatchRequest(url, { is_hidden: false });
      toast.success("Form has been unhidden successfully!");
      updateFormTemplateLocally({ id: template.uuid, type: "unhide" });
      refreshList();
    } catch (err) {
      toast.error("Something went wrong while unhiding form!");
      console.log("err", err);
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      console.log("Deleting template:", template.uuid);

      const url = buildFormTemplateActionUrl("destroy", template.uuid, routeRole);

      await apiDeleteRequest(url);

      toast.success("Form Template Deleted Successfully!");
      updateFormTemplateLocally({ id: template.uuid, type: "delete" });

      refreshList();
    } catch (err) {
      let errorMessage = err?.message || err?.response?.data?.error || err?.response?.data?.message || "Something went wrong while deleting!";
      // Clean up error message by removing trailing ": []" if present
      if (errorMessage && typeof errorMessage === 'string') {
        errorMessage = errorMessage.replace(/\s*:\s*\[\]\s*$/, '');
      }
      toast.error(errorMessage);
      console.log("err", err);
    }
  };

  const handlePublishDraftTemplate = async (templateId) => {
    try {
      if (isUserGracePeriodOver) {
        return;
      }
      setSelectedPublishTemp(templateId);

      const response = roleAccess([USER_ROLE.Super_Admin])
        ? await api.formTemplateDraftToPublish({
            id: templateId,
            role: routeRole,
          })
        : await api.formTemplateDraftToPublishSP({
            id: templateId,
            role: routeRole,
          });

      if (response) {
        setSelectedPublishTemp(null);
        toast.success("Form Template Published Successfully!");
      }
      updateFormTemplateLocally({ id: templateId, type: "delete" });
      setSelectedPublishTemp(null);
    } catch (err) {
      setSelectedPublishTemp(null);
      toast.error(err.message ?? "Something went wrong!");
      console.log("err", err);
    }
  };

  const NON_DELETABLE_FORMS = [
  "ndis service agreement form",
  "behavioural concerns and violence",
  "initial assessment form",
  "live alone participant sole worker risk factor - risk assessment form",
  "medication management",
  "participant acknowledgment form",
  "participant vulnerability",
  "participant consent form",
];

const isNonDeletableForm =
  template?.title &&
  NON_DELETABLE_FORMS.includes(template.title.toLowerCase());

  return (
    <>
      <div className='form-admin-actions'>
        {roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]) &&
          roleAccess(
            [],
            routeRole === "hrm"
              ? PERMISSION_KEYS.HRM_FORM
              : PERMISSION_KEYS.CRM_FORM,
            routeRole === "hrm"
              ? PERMISSION_KEYS_CHILDREN.HRM_FORM.UPDATE
              : PERMISSION_KEYS_CHILDREN.CRM_FORM.UPDATE
          ) &&
          (templateStatus === "draft" ||
            (templateStatus === "publish" && !template.is_hidden)) && (
            <>
              {templateStatus === "publish" ? (
                <>
                  <button
                    className='bg-white hover:bg-green-500 hover:text-white text-green-600 rounded-md'
                    onClick={() => editFormTemplateHandler(template)}
                  >
                    <div className='icon-pencil-wrapper' title='Edit'>
                      <BiPencil className='icon-size' />
                    </div>
                  </button>
                  <button
                    type='button'
                    className='bg-white rounded-md'
                    onClick={() => setShowCloneModal(true)}
                  >
                    <div className='icon-clone-wrapper' title='Clone'>
                      <FaClone className='icon-size' />
                    </div>
                  </button>
                </>
              ) : templateStatus === "draft" ? (
                <>
                  <CustomButton
                    isDisabled={
                      template.id === selectedPublishTemp ||
                      isUserGracePeriodOver
                    }
                    showLoading={template.id === selectedPublishTemp}
                    clickHandler={() => {
                      if (isUserGracePeriodOver) return;
                      setShowConfirmationModalPublish(true);
                    }}
                    label='Publish'
                    Icon={<MdPublish />}
                  />
                  <button
                    disabled={template.id === selectedPublishTemp}
                    className='bg-white hover:bg-green-500 hover:text-white text-green-600 rounded-md'
                    onClick={() => {
                      const encode = window.btoa(encodeURIComponent("draft"));
                      const redirectionUrl =
                        routeRole === "hrm"
                          ? "/hrm/edit-template"
                          : "/crm/edit-template";
                      navigate(`${redirectionUrl}/${template.slug}/${encode}`);
                    }}
                  >
                    <div className='icon-pencil-wrapper' title='Edit'>
                      <BiPencil className='icon-size' />
                    </div>
                  </button>
                </>
              ) : null}
            </>
          )}
        {roleAccess([USER_ROLE.Super_Admin]) &&
          // FIX: Same logic for Super_Admin - allow drafts regardless of is_hidden, restrict published to non-hidden.
          (templateStatus === "draft" ||
            (templateStatus === "publish" && !template.is_hidden)) &&
          (templateStatus === "publish" ? (
            <button
              type='button'
              onClick={() => setShowCloneModal(true)}
            >
              <div className='icon-clone-wrapper' title='Clone'>
                <FaClone className='icon-size' />
              </div>
            </button>
          ) : templateStatus === "draft" ? (
            <>
              <CustomButton
                isDisabled={template.id === selectedPublishTemp}
                showLoading={template.id === selectedPublishTemp}
                clickHandler={() => setShowConfirmationModalPublish(true)}
                label='Publish'
                Icon={<MdPublish />}
              />
              <button
                disabled={template.id === selectedPublishTemp}
                className='bg-white hover:bg-green-500 hover:text-white text-green-600 rounded-md'
                onClick={() => {
                  const encode = window.btoa(encodeURIComponent("draft"));
                  const redirectionUrl =
                    routeRole === "hrm"
                      ? "/hrm/edit-template"
                      : "/crm/edit-template";
                  navigate(`${redirectionUrl}/${template.uuid}/${encode}`);
                }}
              >
                <div className='icon-pencil-wrapper' title='Edit'>
                  <BiPencil className='icon-size' />
                </div>
              </button>
            </>
          ) : null)}
        {roleAccess([
          USER_ROLE.Sp_Admin,
          USER_ROLE.Super_Admin,
          USER_ROLE.Sp_manager,
        ]) && (
          <>
            {roleAccess(
              [],
              routeRole === "hrm"
                ? PERMISSION_KEYS.HRM_FORM
                : PERMISSION_KEYS.CRM_FORM,
              routeRole === "hrm"
                ? PERMISSION_KEYS_CHILDREN.HRM_FORM.VIEW
                : PERMISSION_KEYS_CHILDREN.CRM_FORM.VIEW
            ) && (
              <button onClick={() => viewFormTemplateHandler(template)}>
                <div className='icon-eye-wrapper' title='View'>
                  <HiEye className='icon-size' />
                </div>
              </button>
            )}

            {roleAccess(
              [],
              routeRole === "hrm"
                ? PERMISSION_KEYS.HRM_FORM
                : PERMISSION_KEYS.CRM_FORM,
              routeRole === "hrm"
                ? PERMISSION_KEYS_CHILDREN.HRM_FORM.UPDATE
                : PERMISSION_KEYS_CHILDREN.CRM_FORM.UPDATE
            ) &&
              (templateStatus === "publish" || templateStatus === "hidden") && (
                <>
                  {template.is_hidden ? (
  <button onClick={handleUnhideTemplate}>
    <div className='icon-hide-wrapper' title='Unhide'>
      <HiEye className='icon-size' />
    </div>
  </button>
) : (
  templateStatus === "publish" && (
    <button onClick={handleHideTemplate}>
      <div className='icon-hide-wrapper' title='Hide'>
        <HiEyeOff className='icon-size' />
      </div>
    </button>
  )
)}

                </>
              )}

            {roleAccess(
  [],
  routeRole === "hrm"
    ? PERMISSION_KEYS.HRM_FORM
    : PERMISSION_KEYS.CRM_FORM,
  routeRole === "hrm"
    ? PERMISSION_KEYS_CHILDREN.HRM_FORM.DELETE
    : PERMISSION_KEYS_CHILDREN.CRM_FORM.DELETE
) && (
  <button
  onClick={() => {
    if (routeRole === "crm" && isNonDeletableForm) {
      toast.info(
        `This is a mandatory ${participantLabel} Assessment form and cannot be deleted.`,
        { toastId: "non-deletable-form" }
      );
      return;
    }
    setShowConfirmationModalDelete(true);
  }}
>
  <div className='icon-delete-wrapper' title='Delete'>
    <HiTrash className='icon-size' />
  </div>
</button>

)}
          </>
        )}
      </div>
      {showConfirmationModalPublish && (
        <SweetAlertConfirmationModal
          isOpen={showConfirmationModalPublish}
          title='Confirm your action?'
          content='Are you sure to publish this form?'
          setIsConfirm={(e) => {
            if (e) {
              setShowConfirmationModalPublish(false);
              handlePublishDraftTemplate(template.id);
            } else {
              setShowConfirmationModalPublish(false);
            }
          }}
        />
      )}
      {showConfirmationModalDelete && (
        <SweetAlertConfirmationModal
          isOpen={showConfirmationModalDelete}
          title='Confirm your action?'
          content='Are you sure you want to delete this form? This action cannot be undone.'
          setIsConfirm={(e) => {
            if (e) {
              setShowConfirmationModalDelete(false);
              handleDeleteTemplate(template.uuid);
            } else {
              setShowConfirmationModalDelete(false);
            }
          }}
        />
      )}
      <FormTemplateCloneModal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        template={template}
        role={routeRole}
        routeRole={routeRole}
        onCloned={handleCloneCompleted}
      />
    </>
  );
};

const mapDispatchToProps = {
  updateFormTemplateLocally: updateAllFormTemplatesLocally,
};

const mapStateToprops = (state) => {
  return {};
};

export default connect(
  mapStateToprops,
  mapDispatchToProps
)(FormTemplateListActionButtons);
